/**
 * FireflySystem — 萤火虫粒子系统核心引擎
 *
 * 特性：
 * - 三层粒子（far / mid / near）
 * - 飞行路径：低频正弦漂移 + 高频微动 = 类真实飞行轨迹
 * - 呼吸场：神鹿周围的"活性"脉动
 * - 鼠标扰动
 * - 速度和密度倍率（适配首页 vs 其他页面）
 */

import {
  LAYERS,
  BEHAVIOR,
  type LayerConfig,
  randomFireflyColor,
  getFieldCenter,
} from './FireflyConfig';

export interface Firefly {
  x: number;
  y: number;
  coreSize: number;
  glowRadius: number;
  color: { r: number; g: number; b: number };
  vx: number;
  vy: number;
  baseOpacity: number;
  opacity: number;
  phase: number;
  breathSpeed: number;
  depth: number;
  wanderAngle: number;
  age: number;
  fieldInfluence: number;
  // 飞行路径参数
  driftFreqX: number;   // X 漂移频率
  driftFreqY: number;   // Y 漂移频率
  driftAmpX: number;    // X 漂移幅度
  driftAmpY: number;    // Y 漂移幅度
  driftPhaseX: number;  // X 相位
  driftPhaseY: number;  // Y 相位
  baseX: number;        // 基准位置（漂移围绕此点）
  baseY: number;
}

export interface SystemState {
  width: number;
  height: number;
  scrollProgress: number;
  mouseX: number;
  mouseY: number;
  mouseActive: boolean;
  globalFade: number;
}

export class FireflySystem {
  private ctx: CanvasRenderingContext2D;
  private canvas: HTMLCanvasElement;
  private particles: Firefly[] = [];
  private animId: number | null = null;
  private lastFrame: number;
  private state: SystemState;
  private dpr: number;
  private speedMult: number = 1.0;
  private densityMult: number = 1.0;

  constructor(canvas: HTMLCanvasElement, densityMult: number = 1.0) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) throw new Error('Failed to get 2d context');
    this.ctx = ctx;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.lastFrame = performance.now();
    this.densityMult = densityMult;

    this.state = {
      width: 0,
      height: 0,
      scrollProgress: 0,
      mouseX: -9999,
      mouseY: -9999,
      mouseActive: false,
      globalFade: 1,
    };

    this.resize();
    this.initParticles();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.state.width = rect.width;
    this.state.height = rect.height;
    this.canvas.width = rect.width * this.dpr;
    this.canvas.height = rect.height * this.dpr;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  private initParticles(): void {
    this.particles = [];
    const layerKeys: Array<keyof typeof LAYERS> = ['far', 'mid', 'near'];
    for (const key of layerKeys) {
      const cfg = LAYERS[key];
      const count = Math.max(1, Math.floor(cfg.count * this.densityMult));
      for (let i = 0; i < count; i++) {
        this.particles.push(this.createParticle(cfg));
      }
    }
  }

  private createParticle(cfg: LayerConfig): Firefly {
    const { width, height } = this.state;
    const coreSize = cfg.coreSize[0] + Math.random() * (cfg.coreSize[1] - cfg.coreSize[0]);
    const glowRadius = cfg.glowRadius[0] + Math.random() * (cfg.glowRadius[1] - cfg.glowRadius[0]);
    const angle = Math.random() * Math.PI * 2;
    const speed = cfg.speedRange[0] + Math.random() * (cfg.speedRange[1] - cfg.speedRange[0]);
    const baseX = Math.random() * width;
    const baseY = Math.random() * height;

    return {
      x: baseX,
      y: baseY,
      coreSize,
      glowRadius,
      color: randomFireflyColor(),
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      baseOpacity: cfg.opacityRange[0] + Math.random() * (cfg.opacityRange[1] - cfg.opacityRange[0]),
      opacity: 0,
      phase: Math.random() * Math.PI * 2,
      breathSpeed: cfg.breathRange[0] + Math.random() * (cfg.breathRange[1] - cfg.breathRange[0]),
      depth: cfg.depth,
      wanderAngle: Math.random() * Math.PI * 2,
      age: 0,
      fieldInfluence: 0,
      // 飞行路径 — 每只萤火虫独特的漂移参数
      driftFreqX: 0.0003 + Math.random() * 0.0008,  // 0.3-1.1 mHz
      driftFreqY: 0.0003 + Math.random() * 0.0008,
      driftAmpX: 30 + Math.random() * 60,             // 30-90px
      driftAmpY: 20 + Math.random() * 40,             // 20-60px
      driftPhaseX: Math.random() * Math.PI * 2,
      driftPhaseY: Math.random() * Math.PI * 2,
      baseX,
      baseY,
    };
  }

  // ─── 公共 API ───
  setScrollProgress(p: number): void {
    this.state.scrollProgress = Math.max(0, Math.min(1, p));
  }

  setSpeedMultiplier(v: number): void {
    this.speedMult = v;
  }

  setDensityMultiplier(v: number): void {
    this.densityMult = v;
    // 重新初始化粒子以反映新密度
    this.initParticles();
  }

  setMouse(x: number, y: number): void {
    this.state.mouseX = x;
    this.state.mouseY = y;
    this.state.mouseActive = true;
  }

  clearMouse(): void {
    this.state.mouseActive = false;
  }

  start(): void {
    if (this.animId !== null) return;
    this.lastFrame = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(now - this.lastFrame, 50);
      this.lastFrame = now;
      this.update(dt, now);
      this.render();
      this.animId = requestAnimationFrame(loop);
    };
    this.animId = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.animId !== null) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
  }

  destroy(): void {
    this.stop();
    this.particles = [];
  }

  // ─── 更新 ───
  private update(dt: number, now: number): void {
    const { width, height, scrollProgress } = this.state;

    let globalFade = 1;
    if (BEHAVIOR.fadeOutOnScroll) {
      if (scrollProgress <= BEHAVIOR.scrollFadeStart) globalFade = 1;
      else if (scrollProgress >= BEHAVIOR.scrollFadeEnd) globalFade = 0;
      else {
        const t = (scrollProgress - BEHAVIOR.scrollFadeStart) / (BEHAVIOR.scrollFadeEnd - BEHAVIOR.scrollFadeStart);
        globalFade = 1 - this.easeInOut(t);
      }
    }
    this.state.globalFade = globalFade;

    const center = getFieldCenter(width, height);
    const fieldBreath = 0.5 + 0.5 * Math.sin((now / BEHAVIOR.fieldBreathCycle) * Math.PI * 2);

    for (const p of this.particles) {
      p.age += dt;

      // ── 飞行路径 ──
      // 低频正弦漂移（主体飞行路径）
      const driftX = Math.sin(now * p.driftFreqX + p.driftPhaseX) * p.driftAmpX;
      const driftY = Math.cos(now * p.driftFreqY + p.driftPhaseY) * p.driftAmpY;

      // 高频微动（翅膀振动感的位移）
      const microX = Math.sin(now * 0.002 + p.phase) * 2;
      const microY = Math.cos(now * 0.0025 + p.phase) * 1.5;

      // 合成目标位置 = 基准点 + 漂移 + 微动
      const targetX = p.baseX + driftX + microX;
      const targetY = p.baseY + driftY + microY;

      // 平滑移动到目标（弹簧感速度跟随）
      const smoothing = 0.02 * this.speedMult * (dt / 16);
      p.x += (targetX - p.x) * smoothing;
      p.y += (targetY - p.y) * smoothing;

      // 呼吸场
      const dx = p.x - center.x;
      const dy = p.y - center.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const fieldDist = Math.max(0, 1 - dist / BEHAVIOR.fieldRadius);
      p.fieldInfluence = fieldDist * fieldBreath;

      // 鼠标扰动
      if (this.state.mouseActive) {
        const mx = p.x - this.state.mouseX;
        const my = p.y - this.state.mouseY;
        const mDist = Math.sqrt(mx * mx + my * my);
        if (mDist < BEHAVIOR.mouseRadius && mDist > 0) {
          const force = (1 - mDist / BEHAVIOR.mouseRadius) * BEHAVIOR.mouseForce * (dt / 16);
          p.baseX += (mx / mDist) * force * 50;
          p.baseY += (my / mDist) * force * 50;
        }
      }

      // 边界环绕（当粒子漂出边界时，基准点回到对面）
      if (p.baseX < -50) p.baseX += width + 100;
      else if (p.baseX > width + 50) p.baseX -= width + 100;
      if (p.baseY < -50) p.baseY += height + 100;
      else if (p.baseY > height + 50) p.baseY -= height + 100;

      // 透明度
      const breath = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin((now / p.breathSpeed) * Math.PI * 2 + p.phase));
      const fadeIn = Math.min(1, p.age / BEHAVIOR.fadeInDuration);
      const fieldBoost = 1 + p.fieldInfluence * 0.5;
      p.opacity = Math.min(
        BEHAVIOR.globalMaxOpacity,
        p.baseOpacity * breath * fieldBoost * globalFade * fadeIn
      );
    }
  }

  // ─── 渲染 ───
  private render(): void {
    const w = this.state.width;
    const h = this.state.height;
    this.ctx.clearRect(0, 0, w, h);

    const ctx = this.ctx;

    for (const p of this.particles) {
      if (p.opacity < 0.01) continue;

      const { x, y, coreSize, glowRadius, color, opacity } = p;
      const { r, g, b } = color;

      // 外层光晕
      const glow = ctx.createRadialGradient(x, y, 0, x, y, glowRadius);
      glow.addColorStop(0, `rgba(${r},${g},${b},${opacity * 0.7})`);
      glow.addColorStop(0.15, `rgba(${r},${g},${b},${opacity * 0.4})`);
      glow.addColorStop(0.4, `rgba(${r},${g},${b},${opacity * 0.12})`);
      glow.addColorStop(1, `rgba(${r},${g},${b},0)`);

      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(x, y, glowRadius, 0, Math.PI * 2);
      ctx.fill();

      // 核心点
      ctx.fillStyle = `rgba(${r},${g},${b},${opacity})`;
      ctx.beginPath();
      ctx.arc(x, y, coreSize, 0, Math.PI * 2);
      ctx.fill();

      // 白色高亮核心
      ctx.fillStyle = `rgba(255,255,255,${opacity * 0.5})`;
      ctx.beginPath();
      ctx.arc(x, y, coreSize * 0.35, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private easeInOut(t: number): number {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }
}
